import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Input,
  Label,
  Button,
  Row,
  Col,
  Alert,
  ListGroup,
  ListGroupItem,
  Card,
  CardBody,
} from 'reactstrap';
import {
  callApi,
  formatDate,
  formatAmount,
  formatSignedAmount,
} from '../../utils';
import { showError, showInfo } from '../../actions/notificationActions';
import Loading from '../Loading';

class ManualTopupModal extends Component {
  state = {
    loading: false,
    record: null,
    action: 'approve', // approve or reject
    notes: '',
    searchQuery: '',
    searchLoading: false,
    users: [],
    selectedUser: null,
    submitting: false,
    searchType: 'user', // user or merchant
  };

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen && this.props.recordId) {
      this.fetchDetails();
    }
  }

  fetchDetails = () => {
    this.setState({
      loading: true,
      record: null,
      selectedUser: null,
      notes: '',
      users: [],
      searchQuery: '',
      searchType: 'user',
    });
    callApi(`/manual-topups/${this.props.recordId}`)
      .then(({ data }) => {
        this.setState({
          loading: false,
          record: data,
          // Pre-fill user_id if already approved/processed/applied
          selectedUser: data.user ? data.user : null,
          notes: data.notes || '',
        });
      })
      .catch((err) => {
        this.setState({ loading: false });
        this.props.dispatch(showError(err));
        this.props.toggle();
      });
  };

  handleSearchUsers = (e) => {
    if (e) e.preventDefault();
    const { searchQuery, searchType } = this.state;
    if (!searchQuery.trim()) return;

    this.setState({ searchLoading: true, users: [] });
    const userFilter = JSON.stringify({
      $or: [{ email: { $eq: searchQuery } }, { phone: { $eq: searchQuery } }],
    });
    const endpoint =
      searchType === 'user'
        ? `/users?where=${encodeURIComponent(userFilter)}&page=1&limit=30`
        : `/channels?query=${encodeURIComponent(
            searchQuery
          )}&include[]=businessWallet&page=1&limit=30`;

    callApi(endpoint)
      .then(({ data = [] }) => {
        this.setState({ searchLoading: false, users: data });
      })
      .catch((err) => {
        this.setState({ searchLoading: false });
        this.props.dispatch(showError(err));
      });
  };

  handleSubmit = (e) => {
    if (e) e.preventDefault();
    const { action, notes, selectedUser, submitting, record, searchType } =
      this.state;
    if (submitting || !record) return;

    if (action === 'approve' && !selectedUser) {
      this.props.dispatch(
        showError({ message: 'Please select an account to credit.' })
      );
      return;
    }

    this.setState({ submitting: true });

    let apiCall;
    if (action === 'approve') {
      const payload = {
        notes,
        user: selectedUser ? selectedUser.id : null,
        type: searchType,
      };
      apiCall = callApi(
        `/manual-topups/${record.id}/approval`,
        payload,
        'post'
      );
    } else {
      const payload = {
        data: { notes },
      };
      apiCall = callApi(
        `/manual-topups/${record.id}/approval`,
        payload,
        'delete'
      );
    }

    apiCall
      .then((res) => {
        this.setState({ submitting: false });
        this.props.dispatch(
          showInfo(res.message || 'Manual topup processed successfully!')
        );
        this.props.onSuccess();
        this.props.toggle();
      })
      .catch((err) => {
        this.setState({ submitting: false });
        this.props.dispatch(showError(err));
      });
  };

  handleSearchTypeChange = (type) => {
    this.setState({
      searchType: type,
      searchQuery: '',
      users: [],
      selectedUser: null,
    });
  };

  render() {
    const { isOpen, toggle } = this.props;
    const {
      loading,
      record,
      action,
      notes,
      searchQuery,
      searchLoading,
      users,
      selectedUser,
      submitting,
      searchType,
    } = this.state;

    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg" backdrop="static">
        <ModalHeader toggle={toggle}>
          Manual Topup Details {record ? `#${record.id}` : ''}
        </ModalHeader>
        <ModalBody
          style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}
        >
          {loading && <Loading />}

          {!loading && record && (
            <div>
              {/* Header Info Cards */}
              <Row className="mb-3">
                <Col md={6}>
                  <p className="mb-1 text-muted">Reference</p>
                  <h5 className="font-weight-bold text-monospace">
                    {record.ref}
                  </h5>
                </Col>
                <Col md={6} className="text-right">
                  <p className="mb-1 text-muted">Amount</p>
                  <h4 className="font-weight-bold text-success">
                    {formatAmount(record.amount)}
                  </h4>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={4}>
                  <p className="mb-1 text-muted small">Source Bank</p>
                  <h6>{record.source_bank || 'N/A'}</h6>
                </Col>
                <Col md={4}>
                  <p className="mb-1 text-muted small">Source Type</p>
                  <h6>{record.source_type}</h6>
                </Col>
                <Col md={4}>
                  <p className="mb-1 text-muted small">Created At</p>
                  <h6>{formatDate(record.created_at)}</h6>
                </Col>
              </Row>

              {/* Status Banner */}
              <Alert
                color={
                  record.status === 'PENDING'
                    ? 'warning'
                    : record.status === 'APPROVED' ||
                      record.status === 'APPLIED'
                    ? 'success'
                    : 'danger'
                }
                className="py-2"
              >
                Status: <strong>{record.status}</strong>
                {record.processor &&
                  ` • Processed by: ${
                    record.processor.name || record.processed_by
                  }`}
                {record.processed_at &&
                  ` • Processed at: ${formatDate(record.processed_at)}`}
              </Alert>

              {/* Session ID / Notes */}
              {record.session_id && (
                <div className="mb-3">
                  <span className="text-muted small d-block">Session ID</span>
                  <code className="text-dark bg-light p-1 rounded font-weight-bold">
                    {record.session_id}
                  </code>
                </div>
              )}

              {/* Email Source Detailed Information */}
              {record.source_type === 'EMAIL' && record.source && (
                <Card className="bg-light border mb-3">
                  <CardBody className="p-3">
                    <h6 className="mt-0 mb-2 font-weight-bold text-primary">
                      Email Alert Details
                    </h6>
                    <Row className="mb-2 small">
                      <Col md={6}>
                        <strong>Sender:</strong> {record.source.sender}
                      </Col>
                      <Col md={6}>
                        <strong>Received:</strong> {record.source.received_at}
                      </Col>
                    </Row>
                    <div className="mb-2 small">
                      <strong>Subject:</strong> {record.source.subject}
                    </div>
                    {record.source.raw_body && (
                      <div>
                        <strong className="small">Raw Content:</strong>
                        <div
                          style={{
                            maxHeight: '150px',
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #ddd',
                            padding: '8px',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            marginTop: '4px',
                          }}
                        >
                          {record.source.raw_body}
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              )}

              {/* Processing Panel - only show if PENDING */}
              {record.status === 'PENDING' ? (
                <Card className="border-primary mt-4">
                  <CardBody>
                    <h5 className="mt-0 font-weight-bold">Process Request</h5>

                    <FormGroup>
                      <Label className="font-weight-bold">Action</Label>
                      <div className="d-flex">
                        <FormGroup check className="mr-4">
                          <Label check>
                            <Input
                              type="radio"
                              name="action"
                              checked={action === 'approve'}
                              onChange={() =>
                                this.setState({ action: 'approve' })
                              }
                            />{' '}
                            Approve & Credit User
                          </Label>
                        </FormGroup>
                        <FormGroup check>
                          <Label check>
                            <Input
                              type="radio"
                              name="action"
                              checked={action === 'reject'}
                              onChange={() =>
                                this.setState({ action: 'reject' })
                              }
                            />{' '}
                            Reject Request
                          </Label>
                        </FormGroup>
                      </div>
                    </FormGroup>

                    {/* User/Merchant Selection (Approve mode only) */}
                    {action === 'approve' && (
                      <div className="border p-3 rounded mb-3 bg-white">
                        <FormGroup className="mb-3">
                          <Label className="font-weight-bold">
                            Select Account Type
                          </Label>
                          <div className="d-flex">
                            <FormGroup check className="mr-4 mb-0">
                              <Label check>
                                <Input
                                  type="radio"
                                  name="searchType"
                                  checked={searchType === 'user'}
                                  onChange={() =>
                                    this.handleSearchTypeChange('user')
                                  }
                                />{' '}
                                User
                              </Label>
                            </FormGroup>
                            <FormGroup check className="mb-0">
                              <Label check>
                                <Input
                                  type="radio"
                                  name="searchType"
                                  checked={searchType === 'merchant'}
                                  onChange={() =>
                                    this.handleSearchTypeChange('merchant')
                                  }
                                />{' '}
                                Business (Merchant)
                              </Label>
                            </FormGroup>
                          </div>
                        </FormGroup>

                        <Label className="font-weight-bold">
                          Select{' '}
                          {searchType === 'merchant' ? 'Business' : 'User'} to
                          Credit
                        </Label>

                        <form
                          onSubmit={this.handleSearchUsers}
                          className="d-flex mb-3"
                        >
                          <Input
                            type="text"
                            placeholder={
                              searchType === 'merchant'
                                ? 'Search business name, phone, email...'
                                : 'Search by phone, email, name...'
                            }
                            value={searchQuery}
                            onChange={(e) =>
                              this.setState({ searchQuery: e.target.value })
                            }
                            className="mr-2"
                          />
                          <Button
                            color="secondary"
                            type="submit"
                            disabled={searchLoading || !searchQuery.trim()}
                          >
                            {searchLoading ? 'Searching...' : 'Search'}
                          </Button>
                        </form>

                        {/* Search Results */}
                        {users.length > 0 && (
                          <div
                            className="mb-3"
                            style={{ maxHeight: '200px', overflowY: 'auto' }}
                          >
                            <ListGroup>
                              {users.map((u) => (
                                <ListGroupItem
                                  key={u.id}
                                  active={
                                    selectedUser && selectedUser.id === u.id
                                  }
                                  onClick={() =>
                                    this.setState({ selectedUser: u })
                                  }
                                  className="cursor-pointer py-2 px-3"
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                      <strong>{u.name}</strong>{' '}
                                      <span className="small">({u.phone})</span>
                                      <br />
                                      <span className="small text-muted">
                                        {u.email}
                                      </span>
                                    </div>
                                    {/* A customer wallet can sit negative while
                                        the customer is drawing on an overdraft
                                        facility — show the sign. */}
                                    <span className="badge badge-light">
                                      Bal:{' '}
                                      {formatSignedAmount(
                                        searchType === 'merchant'
                                          ? u.businessWallet.balance
                                          : u.userWallet
                                          ? u.userWallet.balance / 100
                                          : 0
                                      )}
                                    </span>
                                  </div>
                                </ListGroupItem>
                              ))}
                            </ListGroup>
                          </div>
                        )}

                        {/* Selected User Summary */}
                        {selectedUser ? (
                          <Alert color="info" className="mb-0 py-2">
                            <h6 className="alert-heading font-weight-bold mb-1">
                              Selected Target{' '}
                              {searchType === 'merchant' ? 'Business' : 'User'}:
                            </h6>
                            <p className="mb-0 small">
                              <strong>Name:</strong> {selectedUser.name} •{' '}
                              <strong>Phone:</strong> {selectedUser.phone}
                              <br />
                              <strong>ID:</strong> {selectedUser.id}
                              {searchType === 'merchant' &&
                                selectedUser.user_id &&
                                ` (User ID: ${selectedUser.user_id})`}{' '}
                              • <strong>Email:</strong> {selectedUser.email}
                              <br />
                              {searchType === 'merchant' && (
                                <React.Fragment>
                                  <strong>Type:</strong> {selectedUser.type} •{' '}
                                  <strong>Commission:</strong>{' '}
                                  {formatAmount(
                                    selectedUser.businessWallet
                                      .commission_balance
                                  )}
                                  <br />
                                </React.Fragment>
                              )}
                              <strong>Current Balance:</strong>{' '}
                              {formatSignedAmount(
                                searchType === 'merchant'
                                  ? selectedUser.businessWallet.balance
                                  : selectedUser.userWallet
                                  ? selectedUser.userWallet.balance / 100
                                  : 0
                              )}
                            </p>
                          </Alert>
                        ) : (
                          <div className="text-danger small">
                            * Please search and select a{' '}
                            {searchType === 'merchant' ? 'business' : 'user'} to
                            proceed with approval.
                          </div>
                        )}
                      </div>
                    )}

                    <FormGroup className="mb-0">
                      <Label className="font-weight-bold">
                        Notes / Comments
                      </Label>
                      <Input
                        type="textarea"
                        rows="2"
                        placeholder="Add reason for approval/rejection or confirmation details..."
                        value={notes}
                        onChange={(e) =>
                          this.setState({ notes: e.target.value })
                        }
                      />
                    </FormGroup>
                  </CardBody>
                </Card>
              ) : (
                // If already processed, show the recipient details who received the top up
                (record.recipient || record.user) && (
                  <Card className="bg-light border mt-4">
                    <CardBody className="p-3">
                      <h6 className="mt-0 mb-2 font-weight-bold text-success">
                        Recipient Details
                      </h6>
                      {(() => {
                        const rec = record.recipient || record.user;
                        return (
                          <p className="mb-0 small">
                            <strong>Name:</strong> {rec.name} •{' '}
                            <strong>Phone:</strong> {rec.phone || 'N/A'}
                            <br />
                            <strong>ID:</strong> {rec.id} •{' '}
                            <strong>Email:</strong> {rec.email || 'N/A'}
                            {rec.type && (
                              <React.Fragment>
                                • <strong>Type:</strong> {rec.type}
                              </React.Fragment>
                            )}
                            {record.notes && (
                              <React.Fragment>
                                <br />
                                <strong>Notes:</strong> {record.notes}
                              </React.Fragment>
                            )}
                          </p>
                        );
                      })()}
                    </CardBody>
                  </Card>
                )
              )}
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={submitting}>
            Close
          </Button>
          {record && record.status === 'PENDING' && (
            <Button
              color={action === 'approve' ? 'success' : 'danger'}
              onClick={this.handleSubmit}
              disabled={submitting || (action === 'approve' && !selectedUser)}
            >
              {submitting
                ? 'Processing...'
                : action === 'approve'
                ? 'Approve & Credit'
                : 'Reject Request'}
            </Button>
          )}
        </ModalFooter>
      </Modal>
    );
  }
}

export default connect()(ManualTopupModal);
